import { useEffect, useMemo, useState } from "react";

const ROLES_KEY = "tacops_roster_roles_v1";
const SUB_TEAMS_KEY = "tacops_roster_sub_teams_v1";

const DEFAULT_ROLES = [
  "Commander",
  "Team Leader",
  "Entry",
  "Perimeter",
  "Less-Lethal",
  "Breacher",
  "Drone",
  "Medical",
  "Negotiator",
  "Intel"
];

const DEFAULT_SUB_TEAMS = [
  "Command",
  "Entry Team",
  "Perimeter",
  "Less-Lethal",
  "Overwatch",
  "Medical",
  "Drone / UAS"
];

function makeId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function loadList(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.length ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function saveList(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Local storage may be unavailable in some private browsing contexts.
  }
}

export default function RosterManager({ roster, setRoster }) {
  const [roles, setRoles] = useState(() => loadList(ROLES_KEY, DEFAULT_ROLES));
  const [subTeams, setSubTeams] = useState(() => loadList(SUB_TEAMS_KEY, DEFAULT_SUB_TEAMS));
  const [memberPanelOpen, setMemberPanelOpen] = useState(false);
  const [rolesPanelOpen, setRolesPanelOpen] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [newSubTeam, setNewSubTeam] = useState("");
  const [form, setForm] = useState({
    name: "",
    role: "Entry",
    subTeam: "Entry Team",
    position: "",
    status: "Available",
    certs: ""
  });

  useEffect(() => saveList(ROLES_KEY, roles), [roles]);
  useEffect(() => saveList(SUB_TEAMS_KEY, subTeams), [subTeams]);

  const stats = useMemo(() => {
    const total = roster.length;
    const available = roster.filter((op) => op.status === "Available").length;
    const onOperation = roster.filter((op) => op.status === "On Operation").length;
    const unavailable = roster.filter((op) => op.status === "Unavailable").length;
    const training = roster.filter((op) => op.status === "Training").length;
    return { total, available, onOperation, unavailable, training };
  }, [roster]);

  const groupedRoster = useMemo(() => {
    return roster.reduce((groups, operator) => {
      const group = operator.subTeam || "Unassigned";
      if (!groups[group]) groups[group] = [];
      groups[group].push(operator);
      return groups;
    }, {});
  }, [roster]);

  function updateMember(id, field, value) {
    setRoster(roster.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  }

  function deleteMember(id) {
    if (!window.confirm("Remove this member from the roster?")) return;
    setRoster(roster.filter((item) => item.id !== id));
  }

  function addMember(event) {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) return alert("Enter a member name.");

    const nextMember = {
      id: makeId("op"),
      name,
      role: form.role || "Entry",
      subTeam: form.subTeam || "Unassigned",
      position: form.position.trim(),
      status: form.status || "Available",
      certs: form.certs.trim()
    };

    setRoster([nextMember, ...roster]);
    setForm({ name: "", role: form.role, subTeam: form.subTeam, position: "", status: "Available", certs: "" });
  }

  function addRole(event) {
    event.preventDefault();
    const value = newRole.trim();
    if (!value) return;
    if (roles.some((role) => role.toLowerCase() === value.toLowerCase())) return alert("That role already exists.");
    setRoles([...roles, value]);
    setNewRole("");
  }

  function addSubTeam(event) {
    event.preventDefault();
    const value = newSubTeam.trim();
    if (!value) return;
    if (subTeams.some((team) => team.toLowerCase() === value.toLowerCase())) return alert("That sub-team already exists.");
    setSubTeams([...subTeams, value]);
    setNewSubTeam("");
  }

  function removeRole(value) {
    if (!window.confirm(`Remove role: ${value}? Existing members will keep their assigned text.`)) return;
    setRoles(roles.filter((role) => role !== value));
  }

  function removeSubTeam(value) {
    if (!window.confirm(`Remove sub-team: ${value}? Existing members will keep their assigned text.`)) return;
    setSubTeams(subTeams.filter((team) => team !== value));
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Master Roster</h2>
          <p className="muted">Manage operators, availability, roles, positions, sub-teams, and certifications.</p>
        </div>
      </div>

      <div className="dashboard-rings roster-command-rings">
        <div className="dashboard-ring-card tone-blue">
          <span className="ring-glow" />
          <div className="ring-value">{stats.total}</div>
          <div className="ring-label">Total Operators</div>
          <div className="ring-sub">Master roster strength</div>
        </div>

        <div className="dashboard-ring-card tone-green">
          <span className="ring-glow" />
          <div className="ring-value">{stats.available}</div>
          <div className="ring-label">Available</div>
          <div className="ring-sub">Ready for assignment</div>
        </div>

        <div className="dashboard-ring-card tone-gold">
          <span className="ring-glow" />
          <div className="ring-value">{stats.onOperation}</div>
          <div className="ring-label">On Operations</div>
          <div className="ring-sub">Currently committed</div>
        </div>

        <div className="dashboard-ring-card tone-red">
          <span className="ring-glow" />
          <div className="ring-value">{stats.unavailable}</div>
          <div className="ring-label">Unavailable</div>
          <div className="ring-sub">Training: {stats.training}</div>
        </div>
      </div>

      <div className="panel roster-master-panel">
        <div className="section-head roster-master-head">
          <div>
            <h3>Master Roster</h3>
            <p className="muted">Primary roster view stays first. Management forms are collapsed below for a cleaner layout.</p>
          </div>
        </div>

        {roster.length === 0 ? <div className="empty">No roster members yet. Expand Add Member below to create your first operator.</div> : null}

        <div className="roster-group-stack">
          {Object.entries(groupedRoster).map(([subTeam, members]) => (
            <div className="roster-group" key={subTeam}>
              <div className="roster-group-title">
                <span>{subTeam}</span>
                <small>{members.length} member{members.length === 1 ? "" : "s"}</small>
              </div>
              <div className="roster-grid">
                {members.map((operator) => (
                  <div className="roster-card" key={operator.id}>
                    <div className="roster-card-head">
                      <div>
                        <div className="card-title">{operator.name}</div>
                        <small>{operator.position || operator.role || "No position assigned"}</small>
                      </div>
                      <span className={`badge ${operator.status === "Available" ? "good" : operator.status === "Training" ? "warn" : operator.status === "Unavailable" ? "danger" : "neutral"}`}>{operator.status}</span>
                    </div>

                    <div className="form-grid roster-edit-grid">
                      <label>
                        <span>Role</span>
                        <select value={operator.role || ""} onChange={(e) => updateMember(operator.id, "role", e.target.value)}>
                          <option value="">Unassigned</option>
                          {roles.map((role) => <option key={role}>{role}</option>)}
                        </select>
                      </label>
                      <label>
                        <span>Sub-Team</span>
                        <select value={operator.subTeam || ""} onChange={(e) => updateMember(operator.id, "subTeam", e.target.value)}>
                          <option value="">Unassigned</option>
                          {subTeams.map((team) => <option key={team}>{team}</option>)}
                        </select>
                      </label>
                      <label>
                        <span>Position</span>
                        <input value={operator.position || ""} onChange={(e) => updateMember(operator.id, "position", e.target.value)} placeholder="Point, TL, Breacher, Medic..." />
                      </label>
                      <label>
                        <span>Status</span>
                        <select value={operator.status || "Available"} onChange={(e) => updateMember(operator.id, "status", e.target.value)}>
                          <option>Available</option>
                          <option>On Operation</option>
                          <option>Training</option>
                          <option>Unavailable</option>
                        </select>
                      </label>
                    </div>

                    <label className="full roster-certs-field">
                      <span>Certifications / Notes</span>
                      <textarea value={operator.certs || ""} onChange={(e) => updateMember(operator.id, "certs", e.target.value)} placeholder="Entry, breaching, UAS, thermal, less-lethal, medical..." />
                    </label>

                    <div className="roster-card-actions">
                      <button className="danger-text" onClick={() => deleteMember(operator.id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="roster-management-stack">
        <div className="panel roster-accordion-panel">
          <button className="roster-accordion-toggle" onClick={() => setMemberPanelOpen((open) => !open)}>
            <span>Add Member</span>
            <b>{memberPanelOpen ? "−" : "+"}</b>
          </button>

          {memberPanelOpen && (
            <form className="roster-collapse-body" onSubmit={addMember}>
              <div className="form-grid">
                <label>
                  <span>Name / Callsign</span>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Operator name or callsign" />
                </label>
                <label>
                  <span>Role</span>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    {roles.map((role) => <option key={role}>{role}</option>)}
                  </select>
                </label>
                <label>
                  <span>Sub-Team</span>
                  <select value={form.subTeam} onChange={(e) => setForm({ ...form, subTeam: e.target.value })}>
                    {subTeams.map((team) => <option key={team}>{team}</option>)}
                  </select>
                </label>
                <label>
                  <span>Position</span>
                  <input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Entry 1, Perimeter North, Medic 1..." />
                </label>
                <label>
                  <span>Status</span>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option>Available</option>
                    <option>On Operation</option>
                    <option>Training</option>
                    <option>Unavailable</option>
                  </select>
                </label>
                <label className="full">
                  <span>Certifications / Notes</span>
                  <textarea value={form.certs} onChange={(e) => setForm({ ...form, certs: e.target.value })} placeholder="Tactical certs, specialties, limitations, equipment qualifications..." />
                </label>
              </div>
              <div className="modal-actions">
                <button type="submit" className="primary">Add Member</button>
              </div>
            </form>
          )}
        </div>

        <div className="panel roster-accordion-panel">
          <button className="roster-accordion-toggle" onClick={() => setRolesPanelOpen((open) => !open)}>
            <span>Roles / Sub-Teams</span>
            <b>{rolesPanelOpen ? "−" : "+"}</b>
          </button>

          {rolesPanelOpen && (
            <div className="roster-collapse-body roster-tools-grid">
              <div>
                <h3>Roles / Positions</h3>
                <form className="roster-add-row" onSubmit={addRole}>
                  <input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="Add role or position" />
                  <button type="submit" className="primary">Add</button>
                </form>
                <div className="chip-list">
                  {roles.map((role) => (
                    <span className="management-chip" key={role}>{role}<button onClick={() => removeRole(role)} type="button">×</button></span>
                  ))}
                </div>
              </div>

              <div>
                <h3>Sub-Teams</h3>
                <form className="roster-add-row" onSubmit={addSubTeam}>
                  <input value={newSubTeam} onChange={(e) => setNewSubTeam(e.target.value)} placeholder="Add sub-team" />
                  <button type="submit" className="primary">Add</button>
                </form>
                <div className="chip-list">
                  {subTeams.map((team) => (
                    <span className="management-chip" key={team}>{team}<button onClick={() => removeSubTeam(team)} type="button">×</button></span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
